// ============================================
// SIDEBAR - BILEADS (FASE 26)
// ============================================
// ✅ F25B: Link Meta Templates en sección Marketing
// ✅ F26: Sección "Operaciones" (Stock, OTs, Remitos, Facturación, Relevamientos)
// - Items bloqueados VISIBLES (para generar sensación de falta)
// - Barra de progreso de nivel
// - Tooltips explicando bloqueos
// - FASE 19: Rutas admin funcionales
// - FASE 20B: Link Historial de Clientes
// - ✅ F25B: Link Meta Templates en sección Marketing

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationBell from '../components/chat/NotificationBell';  // ✅ HOTFIX: Campana global
import './sidebar.css';

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [currentView, setCurrentView] = useState('client');
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    user, 
    logout, 
    canAccessFeature, 
    getPlanLevel,
    getPlanProgress,
    getPlanInfo 
  } = useAuth();

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  const switchView = (view) => {
    setCurrentView(view);
    if (view === 'client') {
      navigate('/dashboard');
    } else {
      navigate('/admin/dashboard');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Verificar si un item está bloqueado
  const isItemBlocked = (featureKey) => {
    // Sin featureKey = no bloqueado
    if (!featureKey) return false;
    
    // SUPER_ADMIN, ADMIN y VENDEDOR usan el plan del cliente — nunca bloqueados por feature
    // (el plan se comparte a nivel cliente, no a nivel usuario)
    if (['SUPER_ADMIN', 'ADMIN', 'VENDEDOR'].includes(user?.rol)) return false;
    
    return !canAccessFeature(featureKey);
  };

  // Manejar click en item (prevenir navegación si está bloqueado)
  const handleItemClick = (e, item) => {
    if (item.blocked && isItemBlocked(item.featureKey)) {
      e.preventDefault();
      // Opcional: Mostrar modal de upgrade
      console.log(`Feature bloqueada: ${item.name}. Actualiza tu plan.`);
    }
  };

  // Navegación para vista CLIENTE (con info de bloqueo)
  // ✅ F29: Roles por item — si no tiene 'roles', todos ven el item
  const clientNavigation = [
    {
      section: 'Principal',
      items: [
        { 
          name: 'Dashboard', 
          icon: '📊', 
          path: '/dashboard',
          featureKey: null,
          blocked: false
        },
        { 
          name: 'Centro de Mando', 
          icon: '🏛️', 
          path: '/dashboard-ejecutivo',
          featureKey: null,
          blocked: false,
          esDuenoOnly: true
        },
        { 
          name: 'Chat Unificado',
          icon: '💬', 
          path: '/chat',
          featureKey: 'chat',
          blocked: true,
          requiredLevel: 2
        },
        { 
          name: 'Gestión de Leads', 
          icon: '👥', 
          path: '/leads',
          featureKey: 'leads',
          blocked: true,
          requiredLevel: 4
        },
        {
          name: 'Leads Eliminados',
          icon: '🗑️',
          path: '/leads-eliminados',
          featureKey: null,
          blocked: false,
          esDuenoOnly: true
        },
        {
          name: 'Análisis de Leads',
          icon: '📊',
          path: '/analisis-leads',
          featureKey: null,
          blocked: false
        },
        {
          name: 'Alertas de Servicios',
          icon: '🔔',
          path: '/service-alerts',
          featureKey: 'sales',
          blocked: true,
          requiredLevel: 4
        },
      ]
    },
    {
      section: 'Ventas',
      items: [
        { 
          name: 'Pipeline de Ventas', 
          icon: '📈', 
          path: '/pipeline',
          featureKey: 'pipeline',
          blocked: true,
          requiredLevel: 4
        },
        { 
          name: 'Gestión de Ventas', 
          icon: '💰', 
          path: '/sales',
          featureKey: 'sales',
          blocked: true,
          requiredLevel: 4
        },
        { 
          name: 'Control de Pagos', 
          icon: '💳', 
          path: '/payments',
          featureKey: 'payments',
          blocked: true,
          requiredLevel: 4
        },
        { 
          name: 'Historial Clientes', 
          icon: '📋', 
          path: '/historial-clientes',
          featureKey: 'sales',
          blocked: true,
          requiredLevel: 4
        },
        { 
          name: 'Recordatorios', 
          icon: '⏰', 
          path: '/recordatorios',
          featureKey: 'sales',
          blocked: true,
          requiredLevel: 4
        },
      ]
    },
    {
      section: 'Operaciones',
      items: [
        {
          name: 'Stock / Inventario',
          icon: '📦',
          path: '/stock',
          featureKey: 'sales',
          blocked: true,
          requiredLevel: 4
        },
        {
          name: 'Órdenes de Trabajo',
          icon: '🔧',
          path: '/ordenes-trabajo',
          featureKey: 'sales',
          blocked: true,
          requiredLevel: 4
        },
        {
          name: 'Remitos',
          icon: '📄',
          path: '/remitos',
          featureKey: 'sales',
          blocked: true,
          requiredLevel: 4
        },
        {
          name: 'Facturación / Costos',
          icon: '🧾',
          path: '/facturacion',
          featureKey: 'sales',
          blocked: true,
          requiredLevel: 4,
          roles: ['SUPER_ADMIN', 'ADMIN']
        },
        {
          name: 'Relevamientos',
          icon: '🔍',
          path: '/relevamientos',
          featureKey: 'sales',
          blocked: true,
          requiredLevel: 4
        },
        {
          name: 'Informes',
          icon: '📊',
          path: '/informes',
          featureKey: 'sales',
          blocked: true,
          requiredLevel: 4,
          roles: ['SUPER_ADMIN', 'ADMIN']
        },
      ]
    },
    {
      section: 'Marketing',
      roles: ['SUPER_ADMIN', 'ADMIN'],  // ✅ F29: sección completa solo ADMIN+
      items: [
        { 
          name: 'Campañas Meta Ads', 
          icon: '🎯', 
          path: '/campaigns',
          featureKey: 'campaigns',
          blocked: true,
          requiredLevel: 3,
          accesoCampanas: true  // ✅ F-ACCESO-CAMPANAS: VENDEDOR puede ver si tiene permiso
        },
        { 
          name: 'Catálogo Productos', 
          icon: '📦', 
          path: '/productos',
          featureKey: 'campaigns',
          blocked: true,
          requiredLevel: 3,
          roles: ['SUPER_ADMIN', 'ADMIN']  // ✅ F-ACCESO-CAMPANAS: solo ADMIN+ (no va con accesoCampanas)
        },
        { 
          name: 'Broadcasts Masivos', 
          icon: '📢', 
          path: '/broadcasts',
          featureKey: 'broadcasts',
          blocked: true,
          requiredLevel: 5,
          premium: true,
          roles: ['SUPER_ADMIN', 'ADMIN']  // ✅ F-ACCESO-CAMPANAS: solo ADMIN+
        },
      ]
    },
   {
      section: 'Configuración',
      items: [
        { 
          name: 'Configuración de Bots', 
          icon: '🤖', 
          path: '/bots',
          featureKey: 'bot',
          blocked: true,
          requiredLevel: 2,
          roles: ['SUPER_ADMIN', 'ADMIN'],
          accesoBots: true
        },
        {
          name: 'Plantilla Presupuesto',
          icon: '📋',
          path: '/plantilla-presupuesto',
          featureKey: 'sales',
          blocked: true,
          requiredLevel: 4,
          roles: ['SUPER_ADMIN', 'ADMIN']
        },
        { 
          name: 'Meta Templates', 
          icon: '📋', 
          path: '/meta-templates',
          featureKey: 'broadcasts',
          blocked: true,
          requiredLevel: 5,
          premium: true,
          roles: ['SUPER_ADMIN', 'ADMIN']
        },
        {
          name: 'Config. Pagos',
          icon: '🧾',
          path: '/config-pagos',
          featureKey: 'sales',
          blocked: true,
          requiredLevel: 4,
          roles: ['SUPER_ADMIN', 'ADMIN']
        },
      ]
    }
  ];

  // Navegación para vista SUPER ADMIN
  const adminNavigation = [
    {
      section: 'Super Admin',
      items: [
        { name: 'Dashboard Global', icon: '🌐', path: '/admin/dashboard' },
        { name: 'Gestión de Clientes', icon: '🏢', path: '/admin/clients' },
        { name: 'Plantillas de Bots', icon: '🤖', path: '/admin/bot-templates' },
        { name: 'Configuración', icon: '⚙️', path: '/admin/settings' },
      ]
    }
  ];

  // ✅ S11+S28: Navegación limitada para TECNICO — Remitos + OTs
  const tecnicoNavigation = [
    {
      section: 'Operaciones',
      items: [
        {
          name: 'Órdenes de Trabajo',
          icon: '🔧',
          path: '/ordenes-trabajo',
          featureKey: null,
          blocked: false
        },
        {
          name: 'Remitos',
          icon: '📄',
          path: '/remitos',
          featureKey: null,
          blocked: false
        }
      ]
    }
  ];

  const currentNavigation = user?.rol === 'TECNICO'
    ? tecnicoNavigation
    : (currentView === 'client' ? clientNavigation : adminNavigation);


  // Verificar si el usuario puede ver la vista de Super Admin
  const canSwitchToAdmin = user?.rol === 'SUPER_ADMIN';

  // Obtener info del plan para mostrar
  const planInfo = getPlanInfo();
  const planProgress = getPlanProgress();
  const currentLevel = getPlanLevel();

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      {/* Logo y botón de colapsar */}
      <div className="sidebar-header">
        <div className="logo">
          {!collapsed && 'BiLeads'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {/* ✅ HOTFIX: Campana de notificaciones GLOBAL */}
          <NotificationBell />
          <button 
            className="collapse-btn" 
            onClick={toggleSidebar}
            title={collapsed ? 'Expandir' : 'Colapsar'}
          >
            {collapsed ? '»' : '«'}
          </button>
        </div>
      </div>
{/* FASE 14: Barra de progreso del plan — solo visible para SUPER_ADMIN */}
      {!collapsed && currentView === 'client' && user?.rol === 'SUPER_ADMIN' && (
        <div className="plan-progress-container">
          <div className="plan-progress-header">
            <span className="plan-name" style={{ color: planInfo.color }}>
              {planInfo.name}
            </span>
            <span className="plan-level">Nivel {currentLevel}/5</span>
          </div>
          <div className="plan-progress-bar">
            <div 
              className="plan-progress-fill" 
              style={{ 
                width: `${planProgress}%`,
                background: planInfo.color 
              }}
            />
          </div>
          <Link to="/plans" className="upgrade-link">
            {currentLevel < 5 ? '✨ Actualizar Plan' : '🎉 Plan Máximo'}
          </Link>
        </div>
      )}

      {/* Switch Cliente / Super Admin */}
      {!collapsed && canSwitchToAdmin && (
        <div className="user-switch">
          <div className="user-switch-label">Vista actual</div>
          <div className="user-switch-buttons">
            <button
              className={`user-switch-btn ${currentView === 'client' ? 'active' : ''}`}
              onClick={() => switchView('client')}
            >
              Cliente
            </button>
            <button
              className={`user-switch-btn ${currentView === 'admin' ? 'active' : ''}`}
              onClick={() => switchView('admin')}
            >
              Super Admin
            </button>
          </div>
        </div>
      )}

      {/* Navegación */}
      <nav className="sidebar-nav">
        {currentNavigation
          .filter(section => {
            if (!section.roles) {
              // Si no tiene roles de sección, verificar que tenga al menos 1 item visible
              if (section.section === 'Configuración') {
                return ['SUPER_ADMIN', 'ADMIN'].includes(user?.rol) || user?.accesoBots;
              }
              return true;
            }
            // ✅ F-ACCESO-CAMPANAS: Marketing visible si tiene accesoCampanas
            if (section.section === 'Marketing') {
              return section.roles.includes(user?.rol) || user?.accesoCampanas;
            }
            return section.roles.includes(user?.rol);
          })
          .map((section, idx) => (
          <div key={idx} className="nav-section">
            {!collapsed && (
              <div className="nav-section-title">{section.section}</div>
            )}
            {section.items
              .filter(item => {
                if (item.esDuenoOnly) {
                  return user?.rol === 'SUPER_ADMIN' || user?.esDueno === true;
                }
                // Si el item tiene flag accesoBots, también permitir si el usuario tiene el permiso
                if (item.accesoBots && user?.accesoBots) {
                  return true;
                }
                // ✅ F-ACCESO-CAMPANAS: mismo patrón para campañas
                if (item.accesoCampanas && user?.accesoCampanas) {
                  return true;
                }
                return !item.roles || item.roles.includes(user?.rol);
              })
              .map((item, itemIdx) => {
              const blocked = isItemBlocked(item.featureKey);
              const isActive = location.pathname === item.path;

              return (
                <div key={itemIdx} className="nav-item-wrapper">
                  <Link
                    to={blocked ? '#' : item.path}
                    className={`nav-item ${isActive ? 'active' : ''} ${blocked ? 'blocked' : ''}`}
                    title={collapsed ? item.name : ''}
                    onClick={(e) => handleItemClick(e, { ...item, blocked })}
                  >
                    <span className="icon">{item.icon}</span>
                    {!collapsed && (
                      <>
                        <span className="nav-item-name">{item.name}</span>
                        {blocked && (
                          <span className="lock-icon" title={`Requiere nivel ${item.requiredLevel || '?'}`}>
                            🔒
                          </span>
                        )}
                        {item.premium && !blocked && (
                          <span className="premium-badge">PRO</span>
                        )}
                      </>
                    )}
                  </Link>
                  {/* Tooltip de bloqueo */}
                  {blocked && !collapsed && (
                    <div className="blocked-tooltip">
                      Requiere plan nivel {item.requiredLevel || '?'}+
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Usuario y logout */}
      {!collapsed && (
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="avatar">
              {user?.nombre?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
            </div>
            <div className="user-details">
              <div className="user-name">{user?.nombre || 'Usuario'}</div>
              <div className="user-role">
                {currentView === 'admin' ? 'Super Admin' 
                  : user?.rol === 'TECNICO' ? 'Técnico' 
                  : user?.rol || 'Vendedor'}
              </div>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout} title="Cerrar sesión">
            🚪
          </button>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
